import { LearnMoreLink } from '@auctisync/ui';
import { dollarsToCents, formatCents, type Tables } from '@auctisync/shared';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  buildBidderDashboard,
  fetchMyBidLots,
  fetchMyWatchlist,
  invoiceStatusLabel,
  invoiceStatusTone,
  priceIsHidden,
  standingFromYou,
  type LiveFigures,
} from '@auctisync/api-client';
import { Countdown } from '../../components/Countdown';
import { LiveStatusBanner, mostUrgentStatus } from '../../components/bidder-web/LiveStatusBanner';
import { liveRowFrom, saleGroupsFor, useSaleFeeds } from '../../hooks/useSaleFeeds';
import { useServerClock } from '../../hooks/useServerClock';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

/**
 * What `LotList` below actually READS to render a row.
 *
 * Deliberately does NOT include `current_bidder_user_id` / `winning_user_id` —
 * a signed-in bidder cannot read them (2026-09-23), and naming them failed the
 * whole My bids read. The winning/outbid colouring comes from `LotList`'s
 * `tone` PROP, decided by the caller from the server's booleans.
 */
type LotRow = Pick<
  Tables<'lots'>,
  | 'id'
  | 'lot_number'
  | 'title'
  | 'current_bid_cents'
  | 'starting_bid_cents'
  | 'bid_count'
  | 'ends_at'
  | 'status'
  | 'auction_id'
>;

/** A lot the VIEWER is in — with the server's word on whether they lead or won it. */
type LotCard = LotRow & { i_lead?: boolean; i_won?: boolean };

/** The lot columns My bids reads. No bidder ids: the standing arrives as booleans. */
const MY_BID_LOT_COLUMNS =
  'id, lot_number, title, current_bid_cents, starting_bid_cents, bid_count, ends_at, status, auction_id';
/** A row as the page shows it: the live figures laid over (`liveRowFrom`). */
type ShownLot = Omit<LotRow, keyof LiveFigures> & LiveFigures;
type AuctionRef = Pick<Tables<'auctions'>, 'id' | 'slug' | 'title' | 'status' | 'timezone'>;
// Module #23 chunk 2a — entries in the "Following" section of the dashboard.
type FollowedAuction = Pick<Tables<'auctions'>, 'id' | 'slug' | 'title' | 'status' | 'ends_at'>;
type FollowedTenant = Pick<Tables<'tenants'>, 'id' | 'name' | 'display_name' | 'custom_domain'>;
// Module #23 chunk 8 deferred follow-up (c) — entries in the "Smart alerts" section.
type AlertLot = Pick<Tables<'lots'>, 'id' | 'lot_number' | 'title' | 'auction_id'>;
type AlertRow = Pick<
  Tables<'bidder_alerts'>,
  'id' | 'rule_type' | 'target_lot_id' | 'rule_params' | 'last_triggered_at' | 'created_at'
> & {
  lot: AlertLot | null;
};
// Invoices section — the grouped bill (one per auction, generated at close),
// replacing the old per-lot "Won" list so bidders reach their invoices directly.
type InvoiceRow = Pick<
  Tables<'invoices'>,
  'id' | 'invoice_number' | 'total_cents' | 'status' | 'created_at' | 'auction_id'
> & { auctions: Pick<Tables<'auctions'>, 'title'> | null };

export function BidderDashboard() {
  const { session, loading: sessionLoading, userId } = useSession();
  const { nowServerMs } = useServerClock();

  /** Every lot this bidder has bid on, as read. The sections are derived live, below. */
  const [bidLots, setBidLots] = useState<LotCard[]>([]);
  const [watched, setWatched] = useState<LotRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [followedAuctions, setFollowedAuctions] = useState<FollowedAuction[]>([]);
  const [followedTenants, setFollowedTenants] = useState<FollowedTenant[]>([]);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [auctions, setAuctions] = useState<Map<string, AuctionRef>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertActionError, setAlertActionError] = useState<string | null>(null);

  const reloadAlerts = useCallback(async (): Promise<AlertRow[]> => {
    if (userId === null) return [];
    const { data, error: err } = await supabase
      .from('bidder_alerts')
      .select(
        'id, rule_type, target_lot_id, rule_params, last_triggered_at, created_at, lot:lots!target_lot_id(id, lot_number, title, auction_id)',
      )
      .eq('user_id', userId)
      .eq('active', true)
      .order('created_at', { ascending: false });
    if (err) {
      setAlertActionError(err.message);
      return [];
    }
    const list = (data ?? []) as unknown as AlertRow[];
    setAlerts(list);
    return list;
  }, [userId]);

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // My bids — every lot the bidder is in, one entry each, newest first, with
      // the server's standing: the SAME reader the app uses (`fetchMyBidLots`),
      // which also lists a live engine lot whose bids are written at its close.
      const { lots: myBidLots, error: bidErr } = await fetchMyBidLots<LotCard>(
        supabase,
        userId,
        MY_BID_LOT_COLUMNS,
      );
      if (cancelled) return;
      if (bidErr !== null) {
        setError(bidErr);
        setLoading(false);
        return;
      }

      // Watchlist — via `@auctisync/api-client` so the website and the phone
      // share ONE implementation (Run Brief P2.7). The embedded join this
      // replaced pulled the bidder-identity columns, which nothing on this
      // section ever read.
      let watchedLots: LotRow[];
      try {
        watchedLots = (await fetchMyWatchlist(supabase, userId)).lots;
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load your watchlist.');
        setLoading(false);
        return;
      }
      if (cancelled) return;

      // Invoices — RLS scopes to the bidder's own rows across all tenants.
      const { data: invoiceRows, error: invoiceErr } = await supabase
        .from('invoices')
        .select(
          'id, invoice_number, total_cents, status, created_at, auction_id, auctions!invoices_auction_id_fkey(title)',
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (invoiceErr) {
        setError(invoiceErr.message);
        setLoading(false);
        return;
      }
      const invoiceList = (invoiceRows ?? []) as unknown as InvoiceRow[];

      // Module #23 chunk 8 deferred follow-up (c) — load smart alerts.
      const { data: alertRows, error: alertErr } = await supabase
        .from('bidder_alerts')
        .select(
          'id, rule_type, target_lot_id, rule_params, last_triggered_at, created_at, lot:lots!target_lot_id(id, lot_number, title, auction_id)',
        )
        .eq('user_id', userId)
        .eq('active', true)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (alertErr) {
        setError(alertErr.message);
        setLoading(false);
        return;
      }
      const alertList = (alertRows ?? []) as unknown as AlertRow[];

      // Auction refs for linking
      const auctionIds = new Set<string>();
      for (const lot of [...myBidLots, ...watchedLots]) {
        auctionIds.add(lot.auction_id);
      }
      for (const a of alertList) {
        if (a.lot?.auction_id) auctionIds.add(a.lot.auction_id);
      }
      if (auctionIds.size > 0) {
        const { data: auctionRows } = await supabase
          .from('auctions')
          .select('id, slug, title, status, timezone')
          .in('id', Array.from(auctionIds));
        if (!cancelled) {
          const map = new Map<string, AuctionRef>();
          for (const a of (auctionRows ?? []) as AuctionRef[]) {
            map.set(a.id, a);
          }
          setAuctions(map);
        }
      }

      // Module #23 chunk 2a — followed auctions + tenants.
      const { data: followRows } = await supabase
        .from('bidder_follows')
        .select('follow_kind, target_id')
        .eq('user_id', userId);
      if (!cancelled && followRows && followRows.length > 0) {
        const auctionIds = followRows
          .filter((r) => r.follow_kind === 'auction')
          .map((r) => r.target_id);
        const tenantIds = followRows
          .filter((r) => r.follow_kind === 'tenant')
          .map((r) => r.target_id);

        if (auctionIds.length > 0) {
          const { data: faRows } = await supabase
            .from('auctions')
            .select('id, slug, title, status, ends_at')
            .in('id', auctionIds)
            .order('ends_at', { ascending: true, nullsFirst: false });
          if (!cancelled) setFollowedAuctions((faRows ?? []) as FollowedAuction[]);
        }

        if (tenantIds.length > 0) {
          const { data: ftRows } = await supabase
            .from('tenants')
            .select('id, name, display_name, custom_domain')
            .in('id', tenantIds);
          if (!cancelled) setFollowedTenants((ftRows ?? []) as FollowedTenant[]);
        }
      }

      setBidLots(myBidLots);
      setWatched(watchedLots);
      setInvoices(invoiceList);
      setAlerts(alertList);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  /**
   * 🔴 LIVE, AND BY THE SHARED RULES (2026-10-08). This page used to split its
   * lots into winning / outbid / won with its OWN copy of the rule — beside
   * `buildBidderDashboard`, the one the app uses — and only when it loaded, so
   * a bidder outbid while looking at it still saw "I'm winning". Now:
   *   - one live connection per sale with an open lot here (`useSaleFeeds`):
   *     the engine sends a bidder their own lots by itself, and the watched
   *     ones the page names; a Supabase-book sale keeps Realtime;
   *   - prices, clocks and states are the live ones (`liveRowFrom`);
   *   - who leads is the engine's `you` where it has spoken
   *     (`buildBidderDashboard`'s `liveStanding`), so a lot moves from "I'm
   *     winning" to "Outbid" — and to "Won" at the hammer — as it happens.
   */
  const watchedIds = useMemo(() => new Set(watched.map((l) => l.id)), [watched]);
  const saleStatus = useMemo(
    () => new Map([...auctions].map(([id, a]) => [id, a.status] as const)),
    [auctions],
  );
  const groups = useMemo(
    () => saleGroupsFor([...bidLots, ...watched], watchedIds, saleStatus),
    [bidLots, watched, watchedIds, saleStatus],
  );
  const live = useSaleFeeds(groups);
  const urgent = mostUrgentStatus(live.statuses());
  const dash = useMemo(
    () =>
      buildBidderDashboard<ShownLot & Pick<LotCard, 'i_lead' | 'i_won'>>({
        bidLots: bidLots.map((l) => liveRowFrom(l, live)),
        watchedLots: watched.map((l) => liveRowFrom(l, live)),
        nowMs: nowServerMs(),
        liveStanding: (l) => standingFromYou(live.you(l.id)),
      }),
    [bidLots, watched, live, nowServerMs],
  );

  async function handleAlertDelete(id: string, label: string) {
    if (!window.confirm(`Turn off alert: ${label}? This deletes the rule.`)) return;
    setAlertActionError(null);
    const { error: err } = await supabase.from('bidder_alerts').delete().eq('id', id);
    if (err) {
      setAlertActionError(err.message);
      return;
    }
    await reloadAlerts();
  }

  async function handleAlertUpdateThreshold(id: string, dollars: string) {
    const cents = dollars.trim() === '' ? 0 : dollarsToCents(Number(dollars));
    if (!Number.isFinite(cents) || cents <= 0) {
      setAlertActionError('Threshold must be a positive dollar amount.');
      return;
    }
    setAlertActionError(null);
    // Merge into the existing rule_params so bidder-set keys (e.g. window_minutes)
    // survive a threshold edit instead of being clobbered by a full-object overwrite.
    const existingAlert = alerts.find((a) => a.id === id);
    const mergedParams = {
      ...((existingAlert?.rule_params as Record<string, unknown> | null) ?? {}),
      threshold_cents: cents,
    };
    const { error: err } = await supabase
      .from('bidder_alerts')
      .update({ rule_params: mergedParams })
      .eq('id', id);
    if (err) {
      setAlertActionError(err.message);
      return;
    }
    await reloadAlerts();
  }

  if (sessionLoading) return <p className="muted">Loading…</p>;
  if (!session) return <Navigate to="/admin" replace />;

  return (
    <>
      <div className="wa-hd">
        <div>
          <span className="w-lbl">Your account</span>
          <h1 className="w-h1" style={{ fontSize: 32 }}>
            Your dashboard
          </h1>
          <p className="muted" style={{ fontSize: 14, marginBottom: '0.6rem' }}>
            Signed in as <strong style={{ color: 'var(--text)' }}>{session.user.email}</strong>
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <LearnMoreLink helpSlug="bidder-dashboard-overview" newTab>
              How the dashboard works
            </LearnMoreLink>
            <Link to="/my/profile" style={{ fontSize: '0.875rem' }}>
              Edit profile →
            </Link>
            <Link to="/my/payment-methods" style={{ fontSize: '0.875rem' }}>
              Payment methods →
            </Link>
          </div>
        </div>
      </div>

      {/* A sale whose live updates are down says so — the most urgent one, in
          that sale's own time zone. */}
      <LiveStatusBanner
        status={urgent?.info ?? null}
        timeZone={urgent === null ? null : (auctions.get(urgent.saleId)?.timezone ?? null)}
      />

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {loading ? (
        <p className="muted">Loading your dashboard…</p>
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/*
            The stat strip — at-a-glance snapshot of the bidder's active state,
            and the SAME construction as Overview's. `.wa-stat` paints the gaps
            by showing its own `--border` background between cells that each
            carry `--surface-elev`; the cells here are `<span>` because, unlike
            Overview's, there is nowhere further to go — this page already IS
            the list every figure counts.
          */}
          <div className="wa-stat">
            <span>
              <span className="w-lbl">Winning</span>
              <b style={{ color: 'var(--semantic-winning)' }}>{dash.winning.length}</b>
            </span>
            <span>
              <span className="w-lbl">Outbid</span>
              <b style={{ color: dash.outbid.length > 0 ? 'var(--semantic-outbid)' : undefined }}>
                {dash.outbid.length}
              </b>
            </span>
            <span>
              <span className="w-lbl">Watching</span>
              <b>{watched.length}</b>
            </span>
            <span>
              <span className="w-lbl">Won</span>
              <b>{dash.won.length}</b>
            </span>
          </div>

          <Section title="Ending soon" count={dash.endingSoon.length}>
            <LotGrid
              lots={dash.endingSoon}
              auctions={auctions}
              nowServerMs={nowServerMs}
              emptyLabel="Nothing you're watching or winning closes in the next 24 hours."
            />
          </Section>

          <Section title="I'm winning" count={dash.winning.length}>
            <LotGrid
              lots={dash.winning}
              auctions={auctions}
              nowServerMs={nowServerMs}
              tone="winning"
              emptyLabel="You're not the high bidder on any live lots right now."
            />
          </Section>

          <Section title="Outbid" count={dash.outbid.length}>
            <LotGrid
              lots={dash.outbid}
              auctions={auctions}
              nowServerMs={nowServerMs}
              tone="outbid"
              emptyLabel="No outbid lots. Nice job."
            />
          </Section>

          <Section title="Watching" count={dash.watching.length}>
            <LotGrid
              lots={dash.watching}
              auctions={auctions}
              nowServerMs={nowServerMs}
              emptyLabel="No watched lots yet. Tap the heart on any lot to add it here."
            />
          </Section>

          <Section title="Invoices" count={invoices.length}>
            <InvoiceList invoices={invoices} />
          </Section>

          {/* Module #23 chunk 2a — following */}
          <Section title="Following" count={followedAuctions.length + followedTenants.length}>
            <FollowingList auctions={followedAuctions} tenants={followedTenants} />
          </Section>

          {/* Module #23 chunk 8 deferred follow-up (c) — smart alerts management */}
          <Section title="Smart alerts" count={alerts.length}>
            <AlertsList
              alerts={alerts}
              auctions={auctions}
              error={alertActionError}
              onDelete={(id, label) => {
                void handleAlertDelete(id, label);
              }}
              onUpdateThreshold={(id, dollars) => {
                void handleAlertUpdateThreshold(id, dollars);
              }}
            />
          </Section>
        </div>
      )}
    </>
  );
}

function FollowingList({
  auctions,
  tenants,
}: {
  auctions: FollowedAuction[];
  tenants: FollowedTenant[];
}) {
  if (auctions.length === 0 && tenants.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 14, margin: 0 }}>
        Not following anything yet. Tap "Follow" on an auction or seller to get updates here.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {auctions.length > 0 && (
        <div>
          <h3 className="w-lbl" style={{ margin: '0 0 8px' }}>
            Auctions
          </h3>
          <div className="wa-card">
            {auctions.map((a) => (
              <Link key={a.id} to={`/auctions/${a.slug}`} className="wa-row">
                <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{a.title}</span>
                <span className="tag t-n">{a.status}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
      {tenants.length > 0 && (
        <div>
          <h3 className="w-lbl" style={{ margin: '0 0 8px' }}>
            Sellers
          </h3>
          <div className="wa-card">
            {tenants.map((t) => (
              <div key={t.id} className="wa-row">
                <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                  {t.display_name ?? t.name}
                </span>
                {t.custom_domain ? (
                  <a
                    href={`https://${t.custom_domain}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 13 }}
                  >
                    Visit →
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsList({
  alerts,
  auctions,
  error,
  onDelete,
  onUpdateThreshold,
}: {
  alerts: AlertRow[];
  auctions: Map<string, AuctionRef>;
  error: string | null;
  onDelete: (id: string, label: string) => void;
  onUpdateThreshold: (id: string, dollars: string) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  if (alerts.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 14, margin: 0 }}>
        No smart alerts set yet. On any lot page, scroll to <em>Smart alerts</em> to set a price
        threshold or reserve-met notification.
      </p>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {error ? (
        <p
          role="alert"
          style={{
            color: 'var(--text-danger, #991b1b)',
            fontSize: 13.5,
            margin: 0,
          }}
        >
          {error}
        </p>
      ) : null}
      {alerts.map((alert) => {
        const auction = alert.lot ? auctions.get(alert.lot.auction_id) : null;
        const lotLabel = alert.lot
          ? `#${alert.lot.lot_number} · ${alert.lot.title ?? '(untitled)'}`
          : '(lot removed)';
        const href =
          auction && alert.lot ? `/auctions/${auction.slug}/lots/${alert.lot.lot_number}` : null;
        const params = (alert.rule_params ?? {}) as { threshold_cents?: number };
        const ruleDescription =
          alert.rule_type === 'price_below'
            ? `Notify when current bid drops below ${formatCents(params.threshold_cents ?? 0)}`
            : 'Notify when the reserve is met';
        const isEditing = editingId === alert.id && alert.rule_type === 'price_below';
        return (
          <div
            key={alert.id}
            className="wa-card"
            style={{ padding: '12px 16px', display: 'grid', gap: 6 }}
          >
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'baseline',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '1 1 18rem', minWidth: 0 }}>
                {href ? (
                  <Link to={href} style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>
                    {lotLabel}
                  </Link>
                ) : (
                  <span className="muted" style={{ fontSize: 14 }}>
                    {lotLabel}
                  </span>
                )}
                <div className="muted" style={{ fontSize: 13 }}>
                  {auction?.title ?? ''}
                </div>
              </div>
              <span className="tag t-n">
                {alert.rule_type === 'price_below' ? 'Price below' : 'Reserve met'}
              </span>
            </div>
            {isEditing ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="muted" style={{ fontSize: 13.5 }}>
                  Notify below $
                </span>
                <span className="w-field" style={{ width: '7rem' }}>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="decimal"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                </span>
                <button
                  type="button"
                  className="w-btn w-btn-cta w-btn-sm"
                  onClick={() => {
                    onUpdateThreshold(alert.id, draft);
                    setEditingId(null);
                  }}
                  disabled={draft.trim() === ''}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="w-btn w-btn-out w-btn-sm"
                  onClick={() => {
                    setEditingId(null);
                    setDraft('');
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}
              >
                <span className="muted" style={{ fontSize: 13.5 }}>
                  {ruleDescription}
                  {alert.last_triggered_at
                    ? ` · last fired ${new Date(alert.last_triggered_at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}`
                    : ''}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {alert.rule_type === 'price_below' ? (
                    <button
                      type="button"
                      className="w-btn w-btn-out w-btn-sm"
                      onClick={() => {
                        setEditingId(alert.id);
                        setDraft(
                          params.threshold_cents ? (params.threshold_cents / 100).toString() : '',
                        );
                      }}
                    >
                      Edit
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="w-btn w-btn-out w-btn-sm"
                    onClick={() => onDelete(alert.id, lotLabel)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A section is a heading row and its content — not a card.
 *
 * 🔴 THE `tone` PROP IS GONE, AND IT WAS CARRYING NOTHING. `RefinedCard`'s tone
 * tinted the whole section's border green for "I'm winning", red for "Outbid",
 * amber for "Ending soon" — restating what the count beside the heading and the
 * colour on each row already say, in the one place where a coloured border is
 * most likely to be mistaken for a state of its own ("is the whole section in
 * trouble?"). The rows keep their tint; the container stops competing with it.
 */
function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 12,
        }}
      >
        <h2 className="w-lbl" style={{ margin: 0 }}>
          {title}
        </h2>
        <span className="w-pill w-pill-n">{count}</span>
      </header>
      {children}
    </section>
  );
}

function LotGrid({
  lots,
  auctions,
  nowServerMs,
  emptyLabel,
  tone,
}: {
  lots: ShownLot[];
  auctions: Map<string, AuctionRef>;
  nowServerMs: () => number;
  emptyLabel: string;
  tone?: 'winning' | 'outbid';
}) {
  if (lots.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 14, margin: 0 }}>
        {emptyLabel}
      </p>
    );
  }
  /*
   * The standing survives the migration as a LEFT EDGE rather than a full
   * border. `.wa-row` rows sit flush inside one `.wa-card`, so a four-sided
   * tinted border on each would draw a ladder; a 3px edge marks the same thing
   * and is the only part of that border a reader was using.
   */
  const edge =
    tone === 'winning'
      ? 'var(--semantic-winning)'
      : tone === 'outbid'
        ? 'var(--semantic-outbid)'
        : null;
  return (
    <div className="wa-card">
      {lots.map((lot) => {
        const auction = auctions.get(lot.auction_id);
        const href = auction ? `/auctions/${auction.slug}/lots/${lot.lot_number}` : '#';
        return (
          <Link
            key={lot.id}
            to={href}
            className="wa-row"
            style={edge === null ? undefined : { borderLeft: `3px solid ${edge}` }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <b
                style={{
                  display: 'block',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <span className="muted" style={{ fontSize: 13 }}>
                  #{lot.lot_number} ·{' '}
                </span>
                {lot.title ?? '(untitled)'}
              </b>
              <span className="muted" style={{ fontSize: 13 }}>
                {auction?.title ?? 'Auction'}
              </span>
            </span>
            <span style={{ textAlign: 'right' }}>
              <b className="mono" style={{ display: 'block' }}>
                {/* A price the house hides from this bidder is a dash (shared rule). */}
                {priceIsHidden(lot.current_bid_cents, lot.bid_count)
                  ? '—'
                  : formatCents(lot.current_bid_cents ?? lot.starting_bid_cents ?? 0)}
              </b>
              <span className="muted" style={{ fontSize: 13 }}>
                {lot.ends_at && ['active', 'closing', 'scheduled'].includes(lot.status) ? (
                  <Countdown endsAt={lot.ends_at} nowServerMs={nowServerMs} />
                ) : (
                  lot.status
                )}
              </span>
            </span>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Invoice status wording comes from `@auctisync/api-client` (P2.7) so all three
 * surfaces say the same thing about the same bill.
 *
 * They did not. This pill said "Partial" while `/my/invoices` said "Partially
 * paid" — the website disagreeing with ITSELF — and both rendered the raw
 * column value for `void` and `refunded`, so a bidder read the literal word
 * "void" about their own invoice.
 */
function InvoiceStatusPill({ status }: { status: string }) {
  const tone = invoiceStatusTone(status);
  return (
    <span className={`tag ${tone === 'paid' ? 't-nr' : tone === 'due' ? 't-live' : 't-n'}`}>
      {invoiceStatusLabel(status)}
    </span>
  );
}

function InvoiceList({ invoices }: { invoices: InvoiceRow[] }) {
  if (invoices.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 14, margin: 0 }}>
        No invoices yet. When an auction you won lots in closes, its invoice shows up here.
      </p>
    );
  }
  return (
    <div
      className="wa-card"
      style={{
        maxHeight: '22rem',
        overflowY: 'auto',
      }}
    >
      {invoices.map((inv) => (
        <Link key={inv.id} to={`/my/invoices/${inv.id}`} className="wa-row">
          <span style={{ flex: 1, minWidth: 0 }}>
            <b
              style={{
                display: 'block',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {inv.auctions?.title ?? 'Auction'}
            </b>
            <span className="muted mono" style={{ fontSize: 13 }}>
              {inv.invoice_number ?? '—'} · {new Date(inv.created_at).toLocaleDateString()}
            </span>
          </span>
          <b className="mono">{formatCents(inv.total_cents ?? 0)}</b>
          <InvoiceStatusPill status={inv.status} />
        </Link>
      ))}
    </div>
  );
}

/*
 * The one style object left, and it is here on purpose: the bidder site has no
 * error treatment of its own yet, and round 3's brief was explicit — keep these
 * semantics, do not invent a class for them. When the site grows a `.wa-err`,
 * this goes with it.
 */
const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-control, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
  fontSize: 13.5,
};
